import { forwardRef } from "react";
import { DrawerProps as MUIDrawerProps, useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { Close16Icon } from "@kaltura/ds-react-icons";
import { alpha } from "@kaltura/mediaspace-shared-styled";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { StyledCloseButton, StyledContainer, StyledDrawer, StyledTranslucentCloseButton } from "./styles";

export interface DrawerProps extends MUIDrawerProps {
    open: boolean;
    onClose: () => void;
    maxWidth: number;
    backgroundVariant?: "regular" | "translucent";
    contentContainerVariant?: "wide" | "narrow" | "fullWidth";
}

export const drawerClassName = "kms-ds-drawer-root";

/**.
 * a MUI Drawer component with a close button
 */
export const Drawer = forwardRef<HTMLDivElement, DrawerProps>((props: DrawerProps, ref) => {
    const {
        open,
        onClose,
        maxWidth,
        children,
        slotProps,
        backgroundVariant = "regular",
        contentContainerVariant = "wide",
        anchor = "right",
    } = props;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTranslucent = backgroundVariant === "translucent";
    const backgroundVariantStyles = {
        regular: {
            background: theme.kaltura.palette.surfaces.background,
        },
        translucent: {
            background: alpha(theme.kaltura.palette.translucent.commonBlack, theme.kaltura.palette.backdrop.opacity),
            backdropFilter: "blur(48px)",
            color: theme.palette.common.white,
        },
    };

    const closeButton = !isTranslucent ? (
        <StyledCloseButton variant={"borderless"} onClick={onClose} aria-label={translate("close")}>
            <Close16Icon />
        </StyledCloseButton>
    ) : (
        <StyledTranslucentCloseButton
            variant={"borderless"}
            onClick={onClose}
            color={"translucent"}
            aria-label={translate("close")}
        >
            <Close16Icon />
        </StyledTranslucentCloseButton>
    );

    return (
        <StyledDrawer
            ref={ref}
            {...props}
            className={drawerClassName}
            open={open}
            onClose={onClose}
            anchor={anchor}
            slotProps={{
                ...slotProps,
                paper: {
                    ...slotProps?.paper,
                    sx: {
                        overflow: "visible",
                        ...backgroundVariantStyles[backgroundVariant],
                        ...(slotProps?.paper as any)?.sx,
                    },
                },
                backdrop: {
                    ...slotProps?.backdrop,
                    sx: [
                        isTranslucent && {
                            background: "rgba(0, 0, 0, 0)",
                        },
                        (slotProps?.backdrop as any)?.sx,
                    ],
                },
            }}
        >
            {/* On desktop(only when not using translucent background), the close button should be outside the internal modal component*/}
            {!isMobile && !isTranslucent && closeButton}

            <StyledContainer maxWidth={maxWidth} variant={contentContainerVariant}>
                {/* On mobile or when has translucent background, the close button should be under the vertical scroller of the modal content */}
                {(isMobile || isTranslucent) && closeButton}

                {children}
            </StyledContainer>
        </StyledDrawer>
    );
});
